import { useState, useEffect, useCallback } from 'react';
import { Button, Spin, Tag, Empty, notification, Input, Modal } from 'antd';
import {
  AppstoreOutlined, CalendarOutlined, CameraOutlined, CheckOutlined, ClockCircleOutlined,
  EditOutlined, EnvironmentOutlined, EyeOutlined, HomeOutlined, LinkOutlined,
  LoadingOutlined, LockOutlined, PhoneOutlined, PlusOutlined, SearchOutlined,
  ShopOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, ssGet, ssSet, ssInvalidate } from '../shared/utils';
import { useDebounce } from '../shared/hooks';
import { BusinessPhotoUpload, ProfilePhotoUpload } from '../components/CropModal';
import { Section, InfoRow, SectionHeader } from '../components/InfoComponents';
import { countryFlag } from '../shared/utils';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export function OtherBusinessCard({ e, onView }) {
  const location  = [e.current_city, e.district].filter(Boolean).join(', ');
  const allPhotos = e.business_photo ? e.business_photo.split(',').filter(Boolean) : [];
  const cover     = allPhotos[0] || null;
  const initials  = (e.name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: 14, marginBottom: 10, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
      onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'}
    >
      {/* Cover image — taller, with firm name overlay */}
      {cover && (
        <div style={{ height: 150, overflow: 'hidden', position: 'relative', cursor: 'pointer' }} onClick={() => onView(e)}>
          <img src={cover} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)' }} />
          {/* Firm name overlay at bottom */}
          {e.firm_name && (
            <div style={{ position: 'absolute', bottom: 10, left: 12, right: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                {e.firm_name}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scrollable extra photos */}
      {allPhotos.length > 1 && (
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto', padding: '6px 12px',
          background: '#f9fafb', borderBottom: '1px solid #f0f0f0', scrollbarWidth: 'none' }}>
          {allPhotos.map((url, i) => (
            <img key={i} src={url} alt=""
              style={{ height: 48, width: 64, objectFit: 'cover', borderRadius: 6,
                border: i === 0 ? `2px solid ${M}` : '1.5px solid #e5e7eb', flexShrink: 0, cursor: 'pointer' }}
              onClick={() => onView(e)} />
          ))}
        </div>
      )}

      <div style={{ padding: '12px 14px' }}>
        {/* Profile + name row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          {e.profile_photo
            ? <img src={e.profile_photo} alt={e.name}
                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover',
                  objectPosition: 'top', border: `2px solid ${M}`, flexShrink: 0 }} />
            : <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: `${M}15`, border: `2px solid ${M}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 800, color: M,
              }}>{initials}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: '#111827', fontSize: 14 }}>{e.name}</div>
            {e.firm_name && !cover && (
              <div style={{ fontSize: 12, color: M, fontWeight: 700, marginTop: 1 }}>
                <ShopOutlined style={{ marginRight: 4, fontSize: 10 }} />{e.firm_name}
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {e.sector_name && <span style={{ fontSize: 10, background: '#fee2e2', color: M, borderRadius: 20, padding: '2px 9px', fontWeight: 700 }}>{e.sector_name}</span>}
          {e.sub_sector_name && <span style={{ fontSize: 10, background: '#ede9fe', color: '#5b21b6', borderRadius: 20, padding: '2px 9px' }}>{e.sub_sector_name}</span>}
        </div>

        {e.business_about && (
          <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5, marginBottom: 4,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {e.business_about}
          </div>
        )}
        {e.establishment_year && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>स्थापना: {e.establishment_year}</div>}
        {e.experience_years && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>अनुभव: {e.experience_years} वर्ष</div>}
        {e.post && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>💼 {e.post}</div>}
        {e.biz_rank && <div style={{ fontSize: 11, color: M, fontWeight: 600, marginBottom: 2 }}>⭐ {e.biz_rank}</div>}
        {location && (
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
            <EnvironmentOutlined style={{ marginRight: 3, fontSize: 10 }} />{location}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onView(e)} style={{
            flex: 1, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8,
            padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <EyeOutlined /> विवरण
          </button>
          <button onClick={(ev) => { ev.stopPropagation(); onView(e, 'contact'); }} style={{
            flex: 1, background: M, border: 'none', borderRadius: 8,
            padding: '6px 0', fontSize: 12, fontWeight: 700, color: G, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <PhoneOutlined /> संपर्क अनुरोध
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Directory Tab ─────────────────────────────────────────────────────────────

export function AddBusinessPrompt({ onClaimed }) {
  const [claiming,  setClaiming]  = useState(false);
  const [claimed,   setClaimed]   = useState(null);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const res = await axios.post(`${baseUrl}/member/business/claim`, {}, { headers: authHeader() });
      if (res.data.status) {
        setClaimed(res.data);
        notification.success({ message: res.data.message });
        if (res.data.token) localStorage.setItem('memberToken', res.data.token);
        setTimeout(() => { window.location.reload(); }, 1500);
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      if (err.response?.status === 404) {
        notification.info({ message: msg || 'कोई entry नहीं मिली। नया फॉर्म भरें।' });
      } else {
        notification.error({ message: msg || 'Error' });
      }
    } finally { setClaiming(false); }
  };

  if (claimed) {
    return (
      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12,
        padding: '14px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <CheckOutlined style={{ fontSize: 20, color: '#15803d', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>{claimed.firm_name} — जुड़ गई!</div>
          <div style={{ fontSize: 11, color: '#166534' }}>पोर्टल reload हो रहा है…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fafafa', border: '1.5px dashed #d1d5db',
      borderRadius: 12, padding: '14px 16px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <ShopOutlined style={{ fontSize: 20, color: '#9ca3af', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>व्यवसाय नहीं जुड़ा</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>पहले फॉर्म भरा है? नीचे बटन से जोड़ें।</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={handleClaim} disabled={claiming} style={{
          background: M, color: '#fff', border: 'none', borderRadius: 8,
          padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5, opacity: claiming ? 0.7 : 1,
          flexShrink: 0,
        }}>
          {claiming ? <LoadingOutlined /> : <LinkOutlined />}
          {claiming ? 'खोज रहे हैं…' : 'मेरी Listing जोड़ें'}
        </button>
        <a href={`${window.location.origin}/business-directory-form`} target="_blank" rel="noopener noreferrer"
          style={{
            background: '#fff', color: M, border: `1px solid ${M}`, borderRadius: 8,
            padding: '7px 14px', fontSize: 12, fontWeight: 700, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
          }}>
          <PlusOutlined /> नया फॉर्म भरें
        </a>
      </div>
    </div>
  );
}

export function DirectoryTab({ myEntryId, profile, onSwitchTab }) {
  const [entries,     setEntries]     = useState([]);
  const [myImages,    setMyImages]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [total,       setTotal]       = useState(0);
  const [page,          setPage]          = useState(1);
  const [detailEntry,   setDetailEntry]   = useState(null);
  const [detailMode,    setDetailMode]    = useState('view'); // 'view' | 'contact'
  const [imgIdx,        setImgIdx]        = useState(0);
  const [bizContact,    setBizContact]    = useState(null);  // { status, request_id } per open entry
  const [bizPhone,      setBizPhone]      = useState(null);  // revealed phone
  const [sendingBizReq, setSendingBizReq] = useState(false);
  const PAGE_SIZE = 20;
  const debouncedSearch = useDebounce(search, 400);

  // Build image list from business_photo only (profile photo shows in the profile row below)
  useEffect(() => {
    const biz = profile?.business;
    if (!biz) return;
    const photos = (biz.business_photo ? biz.business_photo.split(',').filter(Boolean) : []);
    setMyImages(photos);
    setImgIdx(0);
  }, [profile?.business?.business_photo]); // eslint-disable-line

  const fetchDir = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      const res = await axios.get(`${baseUrl}/member/directory?${params}`, { headers: authHeader() });
      if (res.data.status) {
        setEntries(res.data.result || []);
        setTotal(res.data.total || 0);
        setPage(pg);
      }
    } catch {}
    finally { setLoading(false); }
  }, [debouncedSearch]);

  useEffect(() => { fetchDir(1); }, [fetchDir]);

  const openDetail = useCallback(async (entry, mode = 'view') => {
    setDetailEntry(entry);
    setDetailMode(mode);
    setBizPhone(null);
    setBizContact(null);
    try {
      const res = await axios.get(`${baseUrl}/member/biz-contact-request/status/${entry.id}`, { headers: authHeader() });
      if (res.data.status) {
        setBizContact(res.data.connection);
        if (res.data.connection?.status === 'accepted' || res.data.connection?.status === 'owner') {
          const r2 = await axios.get(`${baseUrl}/member/biz-contact/phone/${entry.id}`, { headers: authHeader() });
          if (r2.data.status) setBizPhone(r2.data.phone);
        }
      }
    } catch {}
  }, []);

  const sendBizContactReq = async () => {
    if (!detailEntry) return;
    setSendingBizReq(true);
    try {
      const res = await axios.post(`${baseUrl}/member/biz-contact-request`, { entry_id: detailEntry.id }, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: 'संपर्क अनुरोध भेजा गया!', placement: 'topRight' });
        setBizContact({ status: 'pending', id: res.data.request_id });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setSendingBizReq(false); }
  };

  // Incoming business contact requests for the logged-in user's business
  const [incomingBizReqs, setIncomingBizReqs] = useState([]);
  useEffect(() => {
    axios.get(`${baseUrl}/member/biz-contact-requests/received`, { headers: authHeader() })
      .then(r => { if (r.data.status) setIncomingBizReqs((r.data.result || []).filter(x => x.status === 'pending')); })
      .catch(() => {});
  }, []); // eslint-disable-line

  const respondBizReq = async (id, action) => {
    try {
      await axios.patch(`${baseUrl}/member/biz-contact-request/${id}`, { action }, { headers: authHeader() });
      notification.success({ message: action === 'accept' ? '✅ संपर्क स्वीकार किया गया!' : 'अस्वीकार किया गया' });
      setIncomingBizReqs(prev => prev.filter(r => r.id !== id));
    } catch { notification.error({ message: 'Failed', placement: 'topRight' }); }
  };

  const myEntry      = entries.find(e => e.id === myEntryId);
  const myBiz        = profile?.business;
  const mySectorId   = myEntry?.sector_id || myBiz?.sector_id;
  const isSearching  = !!debouncedSearch.trim();
  const sameSector   = entries.filter(e => e.id !== myEntryId && String(e.sector_id) === String(mySectorId));
  const otherEntries = entries.filter(e => e.id !== myEntryId && (!mySectorId || String(e.sector_id) !== String(mySectorId)));
  const searchResults = isSearching ? entries.filter(e => e.id !== myEntryId) : [];
  const totalPages   = Math.ceil(total / PAGE_SIZE);

  const imgSrc = (img) => typeof img === 'string' ? img : (img?.url || img?.image_url || '');

  const isSNUser = profile?.type === 'sn_user';
  const hasNoBusiness = isSNUser && !myEntryId && !profile?.business;

  return (
    <div>
      {hasNoBusiness && <AddBusinessPrompt onClaimed={onSwitchTab ? () => onSwitchTab('business') : null} />}

      {/* ── OWN BUSINESS CARD (full details) ── */}
      {(myEntry || myBiz) && (() => {
        const verifStatus = myEntry?.verification_status || myBiz?.verification_status || 'Pending';
        const isPending   = verifStatus !== 'Verified';
        return (
        <div style={{
          background: '#fff',
          border: `2px solid ${isPending ? '#FCD34D' : G}`,
          borderRadius: 14, marginBottom: 20, overflow: 'hidden',
          boxShadow: isPending
            ? '0 4px 20px rgba(180,83,9,0.12)'
            : '0 4px 20px rgba(122,0,0,0.10)',
        }}>
          {/* Header */}
          <div style={{
            background: isPending
              ? 'linear-gradient(135deg, #92400e, #b45309)'
              : `linear-gradient(135deg, ${M}, #b71c1c)`,
            padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: isPending ? '#FDE68A' : G }}>
                आपकी Business Listing
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                Community Directory Entry
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Tag
                color={isPending ? 'gold' : 'green'}
                style={{ margin: 0, fontSize: 10, fontWeight: 700 }}
              >
                {isPending ? '⏳ Review में' : '✓ Verified'}
              </Tag>
              <button onClick={() => onSwitchTab('business')} style={{
                fontSize: 11,
                color: isPending ? '#FDE68A' : G,
                background: 'rgba(255,255,255,0.15)',
                border: `1px solid ${isPending ? 'rgba(253,230,138,0.6)' : 'rgba(255,215,0,0.6)'}`,
                borderRadius: 8, padding: '4px 10px',
                cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <EditOutlined /> {isPending ? 'Update करें' : 'Edit'}
              </button>
            </div>
          </div>

          {/* Pending review notice banner */}
          {isPending && (
            <div style={{
              background: '#FFFBEB', borderBottom: '1px solid #FCD34D',
              padding: '8px 16px', display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <ClockCircleOutlined style={{ color: '#B45309', marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                  Verification Review में है
                </div>
                <div style={{ fontSize: 11, color: '#B45309', marginTop: 1 }}>
                  Admin जल्द verify करेंगे। तब तक आप अपनी listing <strong>देख और update</strong> कर सकते हैं।
                </div>
              </div>
            </div>
          )}

          <div style={{ padding: 16 }}>
            {/* Image carousel */}
            {myImages.length > 0 && (
              <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', height: 320, marginBottom: 14 }}>
                <img src={imgSrc(myImages[imgIdx])} alt="Business"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {myImages.length > 1 && (
                  <>
                    <button onClick={() => setImgIdx(i => (i - 1 + myImages.length) % myImages.length)} style={{
                      position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%',
                      width: 30, height: 30, color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: '30px', textAlign: 'center',
                    }}>‹</button>
                    <button onClick={() => setImgIdx(i => (i + 1) % myImages.length)} style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%',
                      width: 30, height: 30, color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: '30px', textAlign: 'center',
                    }}>›</button>
                    <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
                      {myImages.map((_, i) => (
                        <div key={i} onClick={() => setImgIdx(i)} style={{
                          width: 7, height: 7, borderRadius: '50%', cursor: 'pointer',
                          background: i === imgIdx ? G : 'rgba(255,255,255,0.55)',
                        }} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Profile row + sectors */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
              {(myEntry?.profile_photo || profile?.photo)
                ? <img src={myEntry?.profile_photo || profile?.photo} alt="Profile"
                    style={{ width: 54, height: 54, borderRadius: '50%', objectFit: 'cover',
                      border: `2.5px solid ${G}`, objectPosition: 'top center', flexShrink: 0 }} />
                : <div style={{ width: 54, height: 54, borderRadius: '50%', background: M, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: G }}>
                    {(profile?.name || myEntry?.name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
              }
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>
                  {myEntry?.name || profile?.name || profile?.name_hn}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                  {(myEntry?.sector_name || myBiz?.sector_name) && (
                    <span style={{ fontSize: 11, background: '#fee2e2', color: M, borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                      {myEntry?.sector_name || myBiz?.sector_name}
                    </span>
                  )}
                  {(myEntry?.sub_sector_name || myBiz?.sub_sector_name) && (
                    <span style={{ fontSize: 11, background: '#ede9fe', color: '#5b21b6', borderRadius: 20, padding: '2px 10px' }}>
                      {myEntry?.sub_sector_name || myBiz?.sub_sector_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Full business details */}
            {(myBiz?.firm_name || myEntry?.firm_name) && (
              <InfoRow icon={<ShopOutlined />} label="फर्म / दुकान" value={myBiz?.firm_name || myEntry?.firm_name} />
            )}
            {myBiz?.business_about && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', margin: '8px 0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>व्यवसाय के बारे में</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{myBiz.business_about}</div>
              </div>
            )}
            {myBiz?.establishment_year && (
              <InfoRow icon={<CalendarOutlined />} label="स्थापना वर्ष" value={String(myBiz.establishment_year)} />
            )}
            {myBiz?.business_address && (
              <InfoRow icon={<EnvironmentOutlined />} label="पता" value={myBiz.business_address} />
            )}
            {(myBiz?.current_city || myEntry?.current_city) && (
              <InfoRow icon={<EnvironmentOutlined />} label="शहर / जिला"
                value={[myBiz?.current_city || myEntry?.current_city, myBiz?.district || myEntry?.district].filter(Boolean).join(', ')} />
            )}
            {myBiz?.state && <InfoRow icon={<HomeOutlined />} label="राज्य" value={myBiz.state} />}

            {/* Update button at bottom for pending listings */}
            {isPending && (
              <button
                onClick={() => onSwitchTab('business')}
                style={{
                  marginTop: 14, width: '100%', background: '#92400e',
                  color: '#FDE68A', border: 'none', borderRadius: 9,
                  padding: '9px 0', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6,
                }}
              >
                <EditOutlined /> अपनी Listing Update करें
              </button>
            )}
          </div>
        </div>
        );
      })()}

      {/* ── INCOMING BIZ CONTACT REQUESTS (for business owners) ── */}
      {incomingBizReqs.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#fef3c7', borderBottom: '1px solid #fcd34d', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>📞</span>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
              Business संपर्क अनुरोध ({incomingBizReqs.length})
            </div>
          </div>
          {incomingBizReqs.map(r => (
            <div key={r.id} style={{ padding: '10px 14px', borderBottom: '1px solid #fef3c7', display: 'flex', alignItems: 'center', gap: 10 }}>
              {r.requester_photo
                ? <img src={r.requester_photo} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#d1d5db', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#374151' }}>
                    {(r.requester_name || '?')[0].toUpperCase()}
                  </div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{r.requester_name}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {r.requester_village || ''} · "{r.firm_name || r.biz_name}" का नंबर माँग रहे हैं
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => respondBizReq(r.id, 'accept')}
                  style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  ✓ स्वीकार
                </button>
                <button onClick={() => respondBizReq(r.id, 'decline')}
                  style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 7, padding: '4px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  ✗ नहीं
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── VIEW FULL PUBLIC DIRECTORY LINK ── */}
      <div style={{
        background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
        padding: '8px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      }}>
        <span style={{ fontSize: 12, color: '#92400e' }}>
          <AppstoreOutlined style={{ marginRight: 5 }} />
          पूरी Public Business Directory देखें
        </span>
        <a href="/business-directory-public"
          style={{
            fontSize: 11, fontWeight: 700, color: '#7a0000',
            background: '#fee2e2', border: '1px solid #fca5a5',
            borderRadius: 8, padding: '4px 12px', textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}>
          खोलें →
        </a>
      </div>

      {/* ── SEARCH ── */}
      <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
        placeholder="नाम, फर्म, शहर से खोजें..." value={search}
        onChange={e => setSearch(e.target.value.slice(0, 100))} allowClear
        style={{ borderRadius: 10, marginBottom: 14, height: 42 }} />

      {loading
        ? <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        : <>
            {/* SAME SECTOR */}
            {!isSearching && sameSector.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: M, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ShopOutlined /> इसी क्षेत्र के व्यवसाय ({sameSector.length})
                </div>
                {sameSector.map(e => <OtherBusinessCard key={e.id} e={e} onView={openDetail} />)}
                <div style={{ borderTop: '1.5px dashed #e5e7eb', margin: '16px 0 14px' }} />
              </>
            )}

            {/* OTHER BUSINESSES */}
            {!isSearching && otherEntries.length > 0 && (
              <>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>
                  अन्य व्यवसाय ({otherEntries.length})
                </div>
                {otherEntries.map(e => <OtherBusinessCard key={e.id} e={e} onView={openDetail} />)}
              </>
            )}

            {/* SEARCH RESULTS */}
            {isSearching && (
              <>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>{total} व्यवसाय मिले</div>
                {searchResults.length === 0
                  ? <Empty description="कोई व्यवसाय नहीं मिला" />
                  : searchResults.map(e => <OtherBusinessCard key={e.id} e={e} onView={openDetail} />)
                }
              </>
            )}

            {!isSearching && !sameSector.length && !otherEntries.length && !myEntry && !myBiz && (
              <Empty description="कोई व्यवसाय नहीं मिला" />
            )}

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 14, alignItems: 'center' }}>
                <Button size="small" disabled={page === 1} onClick={() => fetchDir(page - 1)} style={{ borderRadius: 7 }}>← पिछला</Button>
                <span style={{ fontSize: 12, color: '#6b7280' }}>Page {page} / {totalPages}</span>
                <Button size="small" disabled={page >= totalPages} onClick={() => fetchDir(page + 1)} style={{ borderRadius: 7 }}>अगला →</Button>
              </div>
            )}
          </>
      }

      {/* ── DETAIL / CONTACT MODAL ── */}
      <Modal
        open={!!detailEntry}
        onCancel={() => { setDetailEntry(null); setBizContact(null); setBizPhone(null); }}
        footer={null} centered width={440}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShopOutlined style={{ color: M }} />
            <span style={{ color: M, fontWeight: 700 }}>{detailEntry?.firm_name || detailEntry?.name}</span>
          </div>
        }
      >
        {detailEntry && (() => {
          const bizPhotos = detailEntry.business_photo ? detailEntry.business_photo.split(',').filter(Boolean) : [];
          const isOwner   = bizContact?.status === 'owner';
          const accepted  = isOwner || bizContact?.status === 'accepted';
          return (
            <div>
              {/* Photo carousel */}
              {bizPhotos.length > 0 && (
                <div style={{ borderRadius: 10, overflow: 'hidden', height: 180, marginBottom: 14, position: 'relative' }}>
                  <img src={bizPhotos[0]} alt="Business"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {bizPhotos.length > 1 && (
                    <div style={{ position: 'absolute', bottom: 8, right: 10, display: 'flex', gap: 5 }}>
                      {bizPhotos.slice(1).map((url, i) => (
                        <img key={i} src={url} alt=""
                          style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.85)' }} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Header row */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                {detailEntry.profile_photo
                  ? <img src={detailEntry.profile_photo} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}`, flexShrink: 0 }} />
                  : <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#6b7280' }}>
                      {(detailEntry.name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                }
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>{detailEntry.name}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {detailEntry.sector_name && <span style={{ fontSize: 11, background: '#fee2e2', color: M, borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>{detailEntry.sector_name}</span>}
                    {detailEntry.sub_sector_name && <span style={{ fontSize: 11, background: '#ede9fe', color: '#5b21b6', borderRadius: 20, padding: '2px 10px' }}>{detailEntry.sub_sector_name}</span>}
                  </div>
                </div>
              </div>

              {detailEntry.firm_name && <InfoRow icon={<ShopOutlined />} label="फर्म / दुकान" value={detailEntry.firm_name} />}
              {detailEntry.establishment_year && <InfoRow icon={<CalendarOutlined />} label="स्थापना वर्ष" value={String(detailEntry.establishment_year)} />}
              {[detailEntry.current_city, detailEntry.district].filter(Boolean).length > 0 && (
                <InfoRow icon={<EnvironmentOutlined />} label="स्थान" value={[detailEntry.current_city, detailEntry.district].filter(Boolean).join(', ')} />
              )}
              {detailEntry.business_about && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                  {detailEntry.business_about}
                </div>
              )}

              {/* ── CONTACT REQUEST SECTION ── */}
              <div style={{ marginTop: 16, borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                {accepted && bizPhone ? (
                  // Phone revealed
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 4 }}>
                      ✅ संपर्क स्वीकृत — मोबाइल नंबर
                    </div>
                    <a href={`tel:${bizPhone}`} style={{ fontSize: 20, fontWeight: 800, color: '#15803d', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                      📞 {bizPhone}
                    </a>
                    <a href={`https://wa.me/91${bizPhone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                      style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#25d366', color: '#fff', borderRadius: 8, padding: '5px 14px',
                        fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      <WhatsAppOutlined /> WhatsApp पर संपर्क करें
                    </a>
                  </div>
                ) : bizContact?.status === 'pending' ? (
                  // Pending
                  <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ClockCircleOutlined style={{ color: '#b45309', fontSize: 18 }} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>अनुरोध भेजा गया</div>
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>
                        व्यवसाय स्वामी के स्वीकार करने पर नंबर दिखेगा।
                      </div>
                    </div>
                  </div>
                ) : (
                  // Not requested yet
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                      <LockOutlined style={{ marginRight: 6 }} />
                      संपर्क नंबर देखने के लिए अनुरोध भेजें। स्वामी के स्वीकार करने पर दोनों को नंबर दिखेगा।
                    </div>
                    <Button block loading={sendingBizReq} onClick={sendBizContactReq}
                      style={{ background: M, borderColor: M, color: G, borderRadius: 10, height: 42, fontWeight: 700, fontSize: 13 }}>
                      📞 संपर्क नंबर के लिए अनुरोध भेजें
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

// ── My Business Tab ───────────────────────────────────────────────────────────
export function MyBusinessTab({ profile, onUpdated }) {
  const raw    = profile?.type === 'directory' ? profile : profile?.business;
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [sectors,  setSectors]  = useState([]);
  const [form, setForm] = useState(() => ({
    sector_id:          raw?.sector_id          ? String(raw.sector_id) : '',
    sub_sector_id:      raw?.sub_sector_id      ? String(raw.sub_sector_id) : '',
    firm_name:          raw?.firm_name          || '',
    business_about:     raw?.business_about     || '',
    establishment_year: raw?.establishment_year || '',
    business_address:   raw?.business_address   || '',
    current_city:       raw?.current_city       || '',
    district:           raw?.district           || '',
    state:              raw?.state              || '',
    profile_photo:      raw?.profile_photo      || profile?.photo || null,
    business_photo:     raw?.business_photo     || null,
  }));

  useEffect(() => {
    axios.get(`${baseUrl}/business-sector/public-list`)
      .then(r => { if (r.data.status) setSectors(r.data.result || []); })
      .catch(() => {});
  }, []);

  if (!raw) return <Empty description="कोई business listing नहीं मिली" />;
  const status       = raw.verification_status || 'Pending'; // 'Pending' | 'Verified' | 'Rejected'
  const isPending    = status === 'Pending';
  const isVerified   = status === 'Verified';
  const isRejected   = status === 'Rejected';
  const canEdit      = isVerified || isRejected; // Pending = locked
  const activeSector = sectors.find(s => String(s.id) === String(form.sector_id));
  const subSectors   = activeSector?.sub_sectors || [];

  const cancelEdit = () => {
    setForm({
      sector_id:          raw?.sector_id          ? String(raw.sector_id) : '',
      sub_sector_id:      raw?.sub_sector_id      ? String(raw.sub_sector_id) : '',
      firm_name:          raw?.firm_name          || '',
      business_about:     raw?.business_about     || '',
      establishment_year: raw?.establishment_year || '',
      business_address:   raw?.business_address   || '',
      current_city:       raw?.current_city       || '',
      district:           raw?.district           || '',
      state:              raw?.state              || '',
      profile_photo:      raw?.profile_photo      || profile?.photo || null,
      business_photo:     raw?.business_photo     || null,
    });
    setEditing(false);
  };

  const F = (label, key, type = 'text') => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</div>
      <input type={type} value={form[key] || ''}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #d1d5db',
          padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = M}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  );
  const TA = (label, key) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</div>
      <textarea value={form[key] || ''}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        rows={3}
        style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db',
          padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
          resize: 'vertical', fontFamily: 'inherit' }}
        onFocus={e => e.target.style.borderColor = M}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
    </div>
  );

  // Read-only field display
  const ViewRow = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'flex-start' }}>
      <div style={{ fontSize: 12, color: '#9ca3af', minWidth: 120, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{value}</div>
    </div>
  ) : null;

  const save = async () => {
    if (!form.current_city?.trim()) {
      notification.warning({ message: 'शहर (City) दर्ज करना आवश्यक है।', placement: 'topRight' }); return;
    }
    if (!form.district?.trim()) {
      notification.warning({ message: 'जिला (District) दर्ज करना आवश्यक है।', placement: 'topRight' }); return;
    }
    if (!form.state?.trim()) {
      notification.warning({ message: 'राज्य (State) दर्ज करना आवश्यक है।', placement: 'topRight' }); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      sector_id:        form.sector_id     || null,
      sub_sector_id:    form.sub_sector_id || null,
      sector_label:     activeSector?.name || null,
      sub_sector_label: subSectors.find(s => String(s.id) === String(form.sub_sector_id))?.name || null,
    };
    try {
      const res = await axios.patch(`${baseUrl}/member/business`, payload, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: 'अपडेट हो गया!', description: 'Admin जल्द verify करेंगे।', placement: 'topRight' });
        setEditing(false);
        onUpdated();
      } else notification.error({ message: res.data.message, placement: 'topRight' });
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Update failed', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const bizPhotos = form.business_photo ? form.business_photo.split(',').filter(Boolean) : [];

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>My Business Listing</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Tag
            color={isVerified ? 'green' : isRejected ? 'red' : 'orange'}
            style={{ margin: 0 }}>
            {isVerified ? '✓ Verified' : isRejected ? '✗ Rejected' : '⏳ Review में'}
          </Tag>
          {!editing && canEdit && (
            <button onClick={() => setEditing(true)} style={{
              background: M, color: G, border: 'none', borderRadius: 8,
              padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <EditOutlined /> Edit
            </button>
          )}
        </div>
      </div>

      {/* ── Status banners ── */}
      {isPending && !editing && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Admin Review में है</div>
            <div style={{ fontSize: 11, color: '#b45309' }}>जब तक Admin verify नहीं करते, Edit नहीं कर सकते।</div>
          </div>
        </div>
      )}
      {isRejected && !editing && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>✗</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>Rejected — Edit करके दोबारा Submit करें</div>
            {raw.admin_notes && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>कारण: {raw.admin_notes}</div>}
          </div>
          <button onClick={() => setEditing(true)} style={{ background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Edit करें
          </button>
        </div>
      )}

      {/* ── VIEW MODE ── */}
      {!editing && (
        <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

          {/* Cover photo / gradient banner */}
          <div style={{ position: 'relative', background: `linear-gradient(135deg, ${M}22, ${M}44)`, overflow: 'hidden' }}>
            {/* Main cover image */}
            <div style={{ height: bizPhotos.length ? 220 : 80, overflow: 'hidden' }}>
              {bizPhotos.length > 0 && (
                <img src={bizPhotos[0]} alt="Business cover"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              )}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.55) 100%)' }} />
            </div>

            {/* Firm name overlay at bottom of cover image */}
            {raw.firm_name && bizPhotos.length > 0 && (
              <div style={{ position: 'absolute', bottom: 12, left: 16, right: 50 }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: '#fff',
                  textShadow: '0 1px 6px rgba(0,0,0,0.7)', lineHeight: 1.2 }}>
                  {raw.firm_name}
                </div>
              </div>
            )}

            {/* Status badge */}
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <span style={{
                background: isVerified ? 'rgba(21,128,61,0.92)' : isRejected ? 'rgba(185,28,28,0.92)' : 'rgba(180,83,9,0.92)',
                color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px',
                borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                {isVerified ? '✓ Verified' : isRejected ? '✗ Rejected' : '⏳ Review में'}
              </span>
            </div>
          </div>

          {/* Scrollable extra photos strip */}
          {bizPhotos.length > 1 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 16px',
              background: '#f9fafb', borderBottom: '1px solid #e5e7eb', scrollbarWidth: 'none' }}>
              {bizPhotos.map((url, i) => (
                <img key={i} src={url} alt=""
                  style={{ height: 60, width: 80, objectFit: 'cover', borderRadius: 6,
                    border: i === 0 ? `2px solid ${M}` : '2px solid #e5e7eb', flexShrink: 0 }} />
              ))}
            </div>
          )}

          {/* Profile photo + name row */}
          <div style={{ padding: '0 16px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0 10px' }}>
              {form.profile_photo
                ? <img src={form.profile_photo} alt="Profile"
                    style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                      objectPosition: 'top', border: `3px solid ${M}`, flexShrink: 0 }} />
                : <div style={{ width: 56, height: 56, borderRadius: '50%', background: M, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: G }}>
                    {(profile?.name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
              }
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                  {profile?.name}
                </div>
                {!bizPhotos.length && raw.firm_name && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: M, marginTop: 2 }}>{raw.firm_name}</div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                  {raw.sector_name && (
                    <span style={{ fontSize: 11, background: '#fee2e2', color: M, borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                      {raw.sector_name}
                    </span>
                  )}
                  {raw.sub_sector_name && (
                    <span style={{ fontSize: 11, background: '#ede9fe', color: '#5b21b6', borderRadius: 20, padding: '2px 10px' }}>
                      {raw.sub_sector_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* About */}
            {raw.business_about && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                fontSize: 13, color: '#374151', lineHeight: 1.6, borderLeft: `3px solid ${M}` }}>
                {raw.business_about}
              </div>
            )}

            {/* Info rows */}
            <div style={{ marginBottom: 16 }}>
              <ViewRow label="स्थापना वर्ष" value={raw.establishment_year ? String(raw.establishment_year) : null} />
              <ViewRow label="पता"           value={raw.business_address} />
              <ViewRow label="शहर / जिला"   value={[raw.current_city, raw.district].filter(Boolean).join(', ') || null} />
              <ViewRow label="राज्य"         value={raw.state} />
            </div>
          </div>

          {/* Edit button */}
          <div style={{ padding: '0 16px 16px' }}>
            {canEdit ? (
              <button onClick={() => setEditing(true)} style={{
                width: '100%', background: M, color: G,
                border: 'none', borderRadius: 10, padding: '11px 0',
                fontSize: 14, fontWeight: 800, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <EditOutlined /> Edit Listing
              </button>
            ) : (
              <div style={{ width: '100%', background: '#fef9c3', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, color: '#92400e', textAlign: 'center' }}>
                ⏳ Admin Review में है — Edit उपलब्ध नहीं
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── EDIT MODE ── */}
      {editing && (
        <div>
          {isPending && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#b45309' }}>
              Save करने पर status Pending रहेगा। Admin दोबारा verify करेंगे।
            </div>
          )}

          <Section title="फोटो / Photos" icon={<CameraOutlined />}>
            <div style={{ marginBottom: 16 }}>
              <ProfilePhotoUpload
                value={form.profile_photo}
                onChange={v => setForm(f => ({ ...f, profile_photo: v }))}
              />
            </div>
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>दुकान / फर्म फोटो (max 2)</div>
              <BusinessPhotoUpload
                value={form.business_photo}
                onChange={v => setForm(f => ({ ...f, business_photo: v }))}
              />
            </div>
          </Section>

          <Section title="क्षेत्र / Sector" icon={<AppstoreOutlined />}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>क्षेत्र (Sector)</div>
              <select value={form.sector_id}
                onChange={e => setForm(f => ({ ...f, sector_id: e.target.value, sub_sector_id: '' }))}
                style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #d1d5db',
                  padding: '0 12px', fontSize: 13, outline: 'none', background: '#fff', cursor: 'pointer' }}>
                <option value="">-- क्षेत्र चुनें --</option>
                {sectors.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </div>
            {subSectors.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>उप-क्षेत्र (Sub-sector)</div>
                <select value={form.sub_sector_id}
                  onChange={e => setForm(f => ({ ...f, sub_sector_id: e.target.value }))}
                  style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #d1d5db',
                    padding: '0 12px', fontSize: 13, outline: 'none', background: '#fff', cursor: 'pointer' }}>
                  <option value="">-- उप-क्षेत्र चुनें --</option>
                  {subSectors.map(ss => <option key={ss.id} value={String(ss.id)}>{ss.name}</option>)}
                </select>
              </div>
            )}
          </Section>

          <Section title="व्यवसाय विवरण" icon={<ShopOutlined />}>
            {F('फर्म / दुकान का नाम', 'firm_name')}
            {TA('व्यवसाय के बारे में', 'business_about')}
            {F('स्थापना वर्ष', 'establishment_year', 'number')}
          </Section>

          <Section title="पता / Location" icon={<EnvironmentOutlined />}>
            {F('पता (Address)', 'business_address')}
            {F(<span>शहर (City) <span style={{ color: '#ef4444' }}>*</span></span>, 'current_city')}
            {F(<span>जिला (District) <span style={{ color: '#ef4444' }}>*</span></span>, 'district')}
            {F(<span>राज्य (State) <span style={{ color: '#ef4444' }}>*</span></span>, 'state')}
          </Section>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={cancelEdit} style={{
              flex: 1, height: 46, borderRadius: 10, fontWeight: 700, fontSize: 14,
              background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', cursor: 'pointer',
            }}>
              Cancel
            </button>
            <Button loading={saving} onClick={save} icon={<EditOutlined />}
              style={{ flex: 2, height: 46, borderRadius: 10, fontWeight: 700, fontSize: 14,
                background: M, borderColor: M, color: G }}>
              Save Changes
            </Button>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 8 }}>
            Changes will be reviewed by admin before going live.
          </div>
        </div>
      )}
    </div>
  );
}

